import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

type UsePreviewFindOptions = {
  /** Root element that contains rendered preview text */
  containerRef: React.RefObject<HTMLElement | null>
  /** When content identity changes, clear marks and re-apply if open */
  contentKey?: string | null
  enabled?: boolean
  /** Seed query when opening (search panel) */
  initialQuery?: string
}

const MARK_CLASS = 'find-match'
const MARK_ACTIVE = 'find-match--active'

function clearMarks(root: HTMLElement) {
  const marks = root.querySelectorAll(`mark.${MARK_CLASS}`)
  marks.forEach((mark) => {
    const parent = mark.parentNode
    if (!parent) return
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark)
    }
    parent.removeChild(mark)
    parent.normalize()
  })
}

function collectTextNodes(root: HTMLElement): Text[] {
  const nodes: Text[] = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement
      if (!parent) return NodeFilter.FILTER_REJECT
      if (parent.closest('script, style, noscript')) return NodeFilter.FILTER_REJECT
      if (!node.textContent?.trim()) return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })
  let current = walker.nextNode()
  while (current) {
    nodes.push(current as Text)
    current = walker.nextNode()
  }
  return nodes
}

function applyMarks(root: HTMLElement, query: string): HTMLElement[] {
  clearMarks(root)
  const trimmed = query.trim()
  if (!trimmed) return []

  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(escaped, 'gi')
  const matches: HTMLElement[] = []
  const textNodes = collectTextNodes(root)

  for (const textNode of textNodes) {
    const text = textNode.textContent || ''
    regex.lastIndex = 0
    if (!regex.test(text)) continue
    regex.lastIndex = 0

    const frag = document.createDocumentFragment()
    let lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = regex.exec(text)) !== null) {
      if (m.index > lastIndex) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex, m.index)))
      }
      const mark = document.createElement('mark')
      mark.className = MARK_CLASS
      mark.textContent = m[0]
      frag.appendChild(mark)
      matches.push(mark)
      lastIndex = m.index + m[0].length
      if (m[0].length === 0) {
        regex.lastIndex += 1
      }
    }
    if (lastIndex < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex)))
    }
    textNode.parentNode?.replaceChild(frag, textNode)
  }

  return matches
}

function setActiveMatch(matches: HTMLElement[], index: number) {
  matches.forEach((el, i) => {
    el.classList.toggle(MARK_ACTIVE, i === index)
  })
  const active = matches[index]
  if (active) {
    active.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }
}

/**
 * DOM find-in-preview: wrap matches in mark.find-match and cycle with next/prev.
 * Marks are applied in useLayoutEffect so they survive React paint of MarkdownPreview.
 */
export function usePreviewFind({
  containerRef,
  contentKey,
  enabled = true,
  initialQuery = '',
}: UsePreviewFindOptions) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [current, setCurrent] = useState(0)
  const [total, setTotal] = useState(0)
  const [focusRequestId, setFocusRequestId] = useState(0)
  const matchesRef = useRef<HTMLElement[]>([])
  const currentRef = useRef(0)
  const initialAppliedRef = useRef<string | null>(null)

  currentRef.current = current

  const clear = useCallback(() => {
    const root = containerRef.current
    if (root) clearMarks(root)
    matchesRef.current = []
    setTotal(0)
    setCurrent(0)
  }, [containerRef])

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    clear()
    initialAppliedRef.current = null
  }, [clear])

  const openFind = useCallback((seed?: string) => {
    if (!enabled) return
    setOpen(true)
    setFocusRequestId((n) => n + 1)
    if (seed != null) {
      setQuery(seed)
      setCurrent(0)
    }
  }, [enabled])

  const setFindQuery = useCallback((value: string) => {
    setQuery(value)
    setCurrent(0)
  }, [])

  const findNext = useCallback(() => {
    const matches = matchesRef.current
    if (!matches.length) return
    const next = (currentRef.current + 1) % matches.length
    setCurrent(next)
    setActiveMatch(matches, next)
  }, [])

  const findPrev = useCallback(() => {
    const matches = matchesRef.current
    if (!matches.length) return
    const next = (currentRef.current - 1 + matches.length) % matches.length
    setCurrent(next)
    setActiveMatch(matches, next)
  }, [])

  // Apply / refresh marks after DOM paint
  useLayoutEffect(() => {
    const root = containerRef.current
    if (!open || !enabled) {
      if (root) clearMarks(root)
      matchesRef.current = []
      return
    }
    if (!root) return
    if (!query.trim()) {
      clearMarks(root)
      matchesRef.current = []
      setTotal(0)
      return
    }
    const matches = applyMarks(root, query)
    matchesRef.current = matches
    setTotal(matches.length)
    if (matches.length === 0) {
      setCurrent(0)
      return
    }
    const idx = Math.min(currentRef.current, matches.length - 1)
    setCurrent(idx)
    setActiveMatch(matches, idx)
  }, [open, query, contentKey, enabled, containerRef])

  // Seed from initialQuery once content is ready
  useEffect(() => {
    if (!enabled || !initialQuery.trim()) return
    if (!contentKey) return
    const seedKey = `${contentKey}|${initialQuery}`
    if (initialAppliedRef.current === seedKey) return
    initialAppliedRef.current = seedKey
    setOpen(true)
    setFocusRequestId((n) => n + 1)
    setQuery(initialQuery)
    setCurrent(0)
  }, [initialQuery, contentKey, enabled])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const root = containerRef.current
      if (root) clearMarks(root)
    }
  }, [containerRef])

  return {
    open,
    query,
    current,
    total,
    openFind,
    close,
    setFindQuery,
    findNext,
    findPrev,
    focusRequestId,
  }
}
